import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, Search, Users, PhoneCall, TrendingUp,
  Eye, Loader2, LogOut, BarChart3,
  MapPin, Home as HomeIcon, CheckCircle2, XCircle, AlertCircle,
  Webhook, CircleDot, Settings, FileText, Package, Sparkles, KeyRound, ShieldCheck
} from "lucide-react";

import AdminLogin from "@/components/admin/AdminLogin";
import AdminArticles from "@/components/AdminArticles";
import AdminProducts from "@/components/AdminProducts";
import AdminLandings from "@/components/AdminLandings";
import LeadDetailModal from "@/components/admin/LeadDetailModal";
import TestimonialsEditor from "@/components/admin/TestimonialsEditor";
import FooterEditor from "@/components/admin/FooterEditor";
import TrackingPanel from "@/components/admin/TrackingPanel";
import WebhookPanel from "@/components/admin/WebhookPanel";
import ChangePasswordPanel from "@/components/admin/ChangePasswordPanel";
import PrivacyEditor from "@/components/admin/PrivacyEditor";
import AdminAnalytics from "@/components/admin/AdminAnalytics";

const API = "/api";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_solar-config/artifacts/j8etyapd_ChatGPT%20Image%2026%20feb%202026%2C%2011_13_48.png";

const STATUS_MAP = {
  new: { label: "Nuovo", color: "bg-blue-100 text-blue-700", icon: AlertCircle },
  contacted: { label: "Contattato", color: "bg-amber-100 text-amber-700", icon: PhoneCall },
  qualified: { label: "Qualificato", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  proposal_sent: { label: "Proposta Inviata", color: "bg-purple-100 text-purple-700", icon: TrendingUp },
  won: { label: "Vinto", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  lost: { label: "Perso", color: "bg-red-100 text-red-700", icon: XCircle },
};

const PROPERTY_MAP = {
  casa: "Casa Indipendente",
  villetta: "Villetta a Schiera",
  condominio: "Condominio",
  azienda: "Azienda",
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-xl border border-slate-100 p-5 hover:shadow-md transition-shadow" data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center justify-between mb-3">
      <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
        <Icon className="w-5 h-5" strokeWidth={1.5} />
      </div>
    </div>
    <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</div>
    <div className="text-xs text-slate-500 mt-0.5">{label}</div>
  </div>
);

export default function AdminDashboard() {
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterRegion, setFilterRegion] = useState("");
  const [selectedLead, setSelectedLead] = useState(null);
  const [showWebhook, setShowWebhook] = useState(false);
  const [showTracking, setShowTracking] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [activeTab, setActiveTab] = useState("leads");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append("status", filterStatus);
      if (filterRegion) params.append("region", filterRegion);
      if (searchQuery) params.append("search", searchQuery);

      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/leads?${params.toString()}`),
        axios.get(`${API}/stats`),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch {
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterRegion, searchQuery]);

  useEffect(() => {
    if (token) fetchData();
  }, [token, fetchData]);

  const handleLogin = (newToken) => {
    setToken(newToken);
    localStorage.setItem("admin_token", newToken);
  };

  const handleLogout = () => {
    setToken("");
    localStorage.removeItem("admin_token");
  };

  const handleUpdateLead = async (leadId, data) => {
    await axios.put(`${API}/leads/${leadId}`, data);
    fetchData();
  };

  const handleDeleteLead = async (leadId) => {
    await axios.delete(`${API}/leads/${leadId}`);
    toast.success("Lead eliminato");
    fetchData();
  };

  if (!token) return <AdminLogin onLogin={handleLogin} />;

  const uniqueRegions = [...new Set(leads.map((l) => l.region))].filter(Boolean).sort();

  const TABS = [
    { id: "leads", label: "Lead", icon: Users },
    { id: "analytics", label: "Analytics", icon: BarChart3 },
    { id: "articles", label: "Articoli Blog", icon: FileText },
    { id: "products", label: "Prodotti", icon: Package },
    { id: "landings", label: "Landing Promo", icon: Sparkles },
    { id: "footer", label: "Footer", icon: Settings },
    { id: "testimonials", label: "Recensioni", icon: CircleDot },
    { id: "privacy", label: "Privacy", icon: ShieldCheck },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" data-testid="admin-home-link">
              <img src={LOGO_URL} alt="Logo" className="h-10 w-auto" />
            </a>
            <div className="hidden sm:block h-6 w-px bg-slate-200" />
            <span className="hidden sm:block text-sm font-semibold text-slate-700" style={{ fontFamily: "Outfit, sans-serif" }}>
              Dashboard Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowTracking(true)}
              className="flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-2 rounded-lg transition-colors"
              data-testid="admin-tracking-button">
              <BarChart3 className="w-4 h-4" /> Tracking
            </button>
            <button onClick={() => setShowWebhook(true)}
              className="flex items-center gap-1.5 text-sm font-medium text-purple-600 hover:text-purple-700 bg-purple-50 hover:bg-purple-100 px-3 py-2 rounded-lg transition-colors"
              data-testid="admin-webhook-button">
              <Webhook className="w-4 h-4" /> Webhook
            </button>
            <a href="/" className="text-sm text-slate-500 hover:text-emerald-600 flex items-center gap-1 transition-colors" data-testid="admin-back-site">
              <ArrowLeft className="w-4 h-4" /> Torna al sito
            </a>
            <button onClick={() => setShowChangePassword(true)}
              className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-amber-600 transition-colors"
              data-testid="admin-change-password-button">
              <KeyRound className="w-4 h-4" /> Password
            </button>
            <button onClick={handleLogout}
              className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-red-600 transition-colors"
              data-testid="admin-logout-button">
              <LogOut className="w-4 h-4" /> Esci
            </button>
          </div>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-1">
            {TABS.map((tab) => (
              <button key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? "border-emerald-500 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
                data-testid={`admin-tab-${tab.id}`}>
                <tab.icon className="w-4 h-4" /> {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === "leads" ? (
          <>
            {stats && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-testid="admin-stats-grid">
                <StatCard icon={Users} label="Lead Totali" value={stats.total_leads} color="bg-blue-50 text-blue-600" />
                <StatCard icon={PhoneCall} label="Verificati" value={stats.total_verified} color="bg-emerald-50 text-emerald-600" />
                <StatCard icon={TrendingUp} label="Consumo Medio" value={`€${Math.round(stats.avg_consumption)}`} color="bg-amber-50 text-amber-600" />
                <StatCard icon={BarChart3} label="Regioni Attive" value={Object.keys(stats.leads_by_region).length} color="bg-purple-50 text-purple-600" />
              </div>
            )}

            <div className="bg-white rounded-xl border border-slate-100 p-4 mb-6" data-testid="admin-filters">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input placeholder="Cerca per nome, email, telefono..." value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 h-10 rounded-xl" data-testid="admin-search-input" />
                </div>
                <div className="flex gap-3">
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                    className="h-10 rounded-xl border border-input bg-transparent px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring min-w-[140px]"
                    data-testid="admin-filter-status">
                    <option value="">Tutti gli stati</option>
                    {Object.entries(STATUS_MAP).map(([key, val]) => (
                      <option key={key} value={key}>{val.label}</option>
                    ))}
                  </select>
                  <select value={filterRegion} onChange={(e) => setFilterRegion(e.target.value)}
                    className="h-10 rounded-xl border border-input bg-transparent px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring min-w-[140px]"
                    data-testid="admin-filter-region">
                    <option value="">Tutte le regioni</option>
                    {uniqueRegions.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-100 overflow-hidden" data-testid="admin-leads-table">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Lead ({leads.length})
                </h2>
                {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
              </div>

              {leads.length === 0 ? (
                <div className="py-16 text-center" data-testid="admin-no-leads">
                  <Users className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">Nessun lead trovato</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 text-left">
                        <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Nome</th>
                        <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden sm:table-cell">Contatto</th>
                        <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden md:table-cell">Zona</th>
                        <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden lg:table-cell">Immobile</th>
                        <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Stato</th>
                        <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden md:table-cell">Data</th>
                        <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-16"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {leads.map((lead) => {
                        const statusInfo = STATUS_MAP[lead.status] || STATUS_MAP.new;
                        return (
                          <tr key={lead.id} className="hover:bg-slate-50/60 transition-colors" data-testid={`lead-row-${lead.id}`}>
                            <td className="px-5 py-3.5">
                              <div className="font-semibold text-slate-900">{lead.name} {lead.surname}</div>
                            </td>
                            <td className="px-5 py-3.5 hidden sm:table-cell">
                              <div className="text-slate-600 text-xs">{lead.email}</div>
                              <div className="text-slate-500 text-xs flex items-center gap-1">
                                {lead.phone}
                                {lead.phone_verified && <CheckCircle2 className="w-3 h-3 text-emerald-500" />}
                              </div>
                            </td>
                            <td className="px-5 py-3.5 hidden md:table-cell">
                              <div className="flex items-center gap-1 text-xs text-slate-600">
                                <MapPin className="w-3 h-3" /> {lead.province}, {lead.region}
                              </div>
                            </td>
                            <td className="px-5 py-3.5 hidden lg:table-cell">
                              <div className="flex items-center gap-1 text-xs text-slate-600">
                                <HomeIcon className="w-3 h-3" /> {PROPERTY_MAP[lead.property_type] || lead.property_type}
                              </div>
                            </td>
                            <td className="px-5 py-3.5">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${statusInfo.color}`}>
                                <statusInfo.icon className="w-3 h-3" /> {statusInfo.label}
                              </span>
                            </td>
                            <td className="px-5 py-3.5 hidden md:table-cell text-xs text-slate-400">
                              {lead.created_at}
                            </td>
                            <td className="px-5 py-3.5">
                              <button onClick={() => setSelectedLead(lead)}
                                className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-emerald-600 transition-colors"
                                data-testid={`lead-view-${lead.id}`}>
                                <Eye className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        ) : activeTab === "analytics" ? (
          <AdminAnalytics />
        ) : activeTab === "articles" ? (
          <AdminArticles />
        ) : activeTab === "products" ? (
          <AdminProducts />
        ) : activeTab === "footer" ? (
          <FooterEditor />
        ) : activeTab === "testimonials" ? (
          <TestimonialsEditor />
        ) : activeTab === "privacy" ? (
          <PrivacyEditor />
        ) : (
          <AdminLandings />
        )}
      </div>

      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onUpdate={handleUpdateLead}
          onDelete={handleDeleteLead}
        />
      )}

      {showWebhook && <WebhookPanel onClose={() => setShowWebhook(false)} />}
      {showTracking && <TrackingPanel onClose={() => setShowTracking(false)} />}
      {showChangePassword && <ChangePasswordPanel onClose={() => setShowChangePassword(false)} />}
    </div>
  );
}
